/**
 * <TakeActionPanel> — buyer's per-finding remediation status control.
 *
 * Renders inline under every actionable finding in the /Recon report
 * viewer. Buyer picks one of five states (acknowledged / in_progress /
 * fixed / accepted_risk / needs_help), optionally adds a note (used
 * primarily with accepted_risk to capture the business reasoning),
 * and the panel POSTs to /api/v1/client-portal/findings/{id}/action.
 *
 * Append-only on the backend: each click writes a new state row; the
 * report-viewer reads "current state" via the
 * client_finding_action_current view.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recordFindingActionApiV1ClientPortalFindingsEvidenceCardIdActionPost } from "@hackereyes/api-client";
import "./take-action-panel.css";

export type ActionState =
  | "acknowledged"
  | "in_progress"
  | "fixed"
  | "accepted_risk"
  | "needs_help";

const STATE_META: Record<ActionState, { label: string; tone: string; desc: string }> = {
  acknowledged: {
    label: "Acknowledged",
    tone: "slate",
    desc: "I've seen this, considering options.",
  },
  in_progress: {
    label: "In progress",
    tone: "cyan",
    desc: "Actively working on a fix.",
  },
  fixed: {
    label: "Fixed",
    tone: "green",
    desc: "I believe this is resolved — re-test on next scan.",
  },
  accepted_risk: {
    label: "Accept risk",
    tone: "amber",
    desc: "Won't fix — business decision (add a note).",
  },
  needs_help: {
    label: "Needs help",
    tone: "flame",
    desc: "Flag for the assessor to advise.",
  },
};

interface TakeActionPanelProps {
  evidenceCardId: string;
  caseId: string;
  currentState: ActionState | null;
  currentNote: string | null;
  currentFixByDate?: string | null;
}

export function TakeActionPanel({
  evidenceCardId,
  caseId,
  currentState,
  currentNote,
  currentFixByDate,
}: TakeActionPanelProps) {
  const queryClient = useQueryClient();
  const [draftState, setDraftState] = useState<ActionState | null>(currentState);
  const [draftNote, setDraftNote] = useState(currentNote ?? "");
  const [draftFixBy, setDraftFixBy] = useState<string>(currentFixByDate ?? "");
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!draftState) throw new Error("Pick a state.");
      // fix_by_date is only meaningful with in_progress. Strip on others
      // so the buyer can flip states freely without dragging a stale date.
      const fixBy =
        draftState === "in_progress" && draftFixBy ? draftFixBy : null;
      const { data, error } =
        await recordFindingActionApiV1ClientPortalFindingsEvidenceCardIdActionPost({
          path: { evidence_card_id: evidenceCardId },
          body: {
            state: draftState,
            note: draftNote.trim() || null,
            fix_by_date: fixBy,
          },
        });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new Error(detail || "Couldn't save action.");
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finding-actions", caseId] });
      setOpen(false);
    },
  });

  const hasUnsavedChanges =
    draftState !== currentState ||
    (draftNote.trim() || null) !== (currentNote?.trim() || null) ||
    (draftState === "in_progress" &&
      (draftFixBy || null) !== (currentFixByDate || null));

  // Today's date in YYYY-MM-DD for the min attribute — date <input> uses
  // local time, so this matches what the buyer sees on their calendar.
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="take-action">
      <div className="take-action-summary">
        {currentState ? (
          <span className={`take-action-pill take-action-pill-${STATE_META[currentState].tone}`}>
            {STATE_META[currentState].label}
          </span>
        ) : (
          <span className="take-action-pill take-action-pill-empty">No action taken</span>
        )}
        <button
          type="button"
          className="take-action-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Cancel" : currentState ? "Update" : "Take action"}
        </button>
      </div>

      {open && (
        <div className="take-action-form">
          <div className="take-action-options" role="radiogroup">
            {(Object.keys(STATE_META) as ActionState[]).map((s) => {
              const meta = STATE_META[s];
              const selected = draftState === s;
              return (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`take-action-option take-action-option-${meta.tone}${selected ? " is-selected" : ""}`}
                  onClick={() => setDraftState(s)}
                >
                  <span className="take-action-option-label">{meta.label}</span>
                  <span className="take-action-option-desc">{meta.desc}</span>
                </button>
              );
            })}
          </div>

          {draftState === "in_progress" && (
            <div className="take-action-fixby">
              <label
                htmlFor={`fixby-${evidenceCardId}`}
                className="take-action-fixby-label"
              >
                Target fix-by date <span className="muted">(optional)</span>
              </label>
              <input
                id={`fixby-${evidenceCardId}`}
                type="date"
                className="take-action-fixby-input"
                min={todayIso}
                value={draftFixBy}
                onChange={(e) => setDraftFixBy(e.target.value)}
              />
              <p className="take-action-fixby-hint">
                Setting a date opts you into one deadline-approaching
                reminder. Leave blank if no specific timeline applies.
              </p>
              {draftFixBy && (
                <button
                  type="button"
                  className="take-action-fixby-clear"
                  onClick={() => setDraftFixBy("")}
                >
                  Clear date
                </button>
              )}
            </div>
          )}

          {(draftState === "accepted_risk" || draftState === "needs_help") && (
            <div className="take-action-note">
              <label htmlFor={`note-${evidenceCardId}`} className="take-action-note-label">
                {draftState === "accepted_risk"
                  ? "Reason for accepting (recorded with the report)"
                  : "What do you need help with?"}
              </label>
              <textarea
                id={`note-${evidenceCardId}`}
                className="take-action-note-input"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                placeholder={
                  draftState === "accepted_risk"
                    ? "e.g. compensating control via WAF rule X, scheduled for next quarter…"
                    : "e.g. unclear how to validate the fix on our hosting provider…"
                }
                rows={3}
                maxLength={2000}
              />
            </div>
          )}

          {mutation.isError && (
            <p className="take-action-error">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="take-action-actions">
            <button
              type="button"
              className="recon-btn recon-btn-primary"
              disabled={
                !draftState ||
                !hasUnsavedChanges ||
                mutation.isPending
              }
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? "Saving…" : "Save action"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
